/**
 * Tip DJ Component
 * Quick tip buttons + custom amount for tipping the DJ with VibeCoins
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Coins, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../utils/tokenStorage';

const TIP_AMOUNTS = [10, 50, 100, 500, 1000];

const TipDJModal = ({ isOpen, onClose, vibecoins = 0, onTipSent }) => {
  const [customAmount, setCustomAmount] = useState('');
  const [sending, setSending] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  if (!isOpen) return null;

  const sendTip = async (amount) => {
    if (amount < 1) return;
    if (amount > vibecoins) {
      toast.error(`Not enough VibeCoins (need ${amount}, have ${vibecoins})`);
      return;
    }
    setSending(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_URL}/api/nightclub/tip-dj`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ amount })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        onTipSent?.(data.new_balance);
        setCustomAmount('');
      } else {
        toast.error(data.detail || 'Failed to tip');
      }
    } catch (e) {
      toast.error('Failed to send tip');
    }
    setSending(false);
  };

  return (
    <div
      className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
      data-testid="tip-dj-overlay"
    >
      <Card
        className="w-full max-w-sm bg-gray-900 border-yellow-500/30"
        onClick={e => e.stopPropagation()}
        data-testid="tip-dj-modal"
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Coins className="w-5 h-5 text-yellow-400" />
              Tip the DJ
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-tip-dj">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-white/50 text-sm">Show love with VibeCoins</p>
          <div className="flex items-center gap-1.5 mt-1">
            <Coins className="w-4 h-4 text-yellow-400" />
            <span className="text-yellow-400 font-bold text-sm">{vibecoins.toLocaleString()}</span>
            <span className="text-white/40 text-xs">available</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Quick tip buttons */}
          <div className="grid grid-cols-5 gap-2">
            {TIP_AMOUNTS.map(amount => (
              <button
                key={amount}
                onClick={() => sendTip(amount)}
                disabled={sending || vibecoins < amount}
                className={`p-2 rounded-lg border text-center transition-all ${
                  vibecoins >= amount
                    ? 'border-yellow-500/30 bg-yellow-500/10 hover:bg-yellow-500/20 hover:border-yellow-400 text-yellow-400'
                    : 'border-gray-700 bg-gray-800/50 text-gray-600 cursor-not-allowed'
                }`}
                data-testid={`tip-${amount}-btn`}
              >
                <div className="text-sm font-bold">{amount >= 1000 ? `${amount / 1000}k` : amount}</div>
              </button>
            ))}
          </div>

          {/* Custom amount */}
          <div className="flex gap-2">
            <Input
              type="number"
              min="1"
              max="10000"
              value={customAmount}
              onChange={e => setCustomAmount(e.target.value)}
              placeholder="Custom amount..."
              className="bg-black/30 border-yellow-500/30 text-white flex-1"
              data-testid="custom-tip-input"
            />
            <Button
              onClick={() => sendTip(parseInt(customAmount) || 0)}
              disabled={sending || !customAmount || parseInt(customAmount) < 1}
              className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-400 hover:to-amber-400 text-black font-bold"
              data-testid="send-custom-tip-btn"
            >
              <Zap className="w-4 h-4 mr-1" />
              Tip
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TipDJModal;
